// Equipo digital comercial y gobierno comercial (docs/CONTRATO.md §13.9; backend/app/api/comercial.py).
import { solicitar } from './http';
import type { Actividad, AprobacionComercial, Contacto, Etapa, Moneda, Negociacion, NegociacionVista, NivelRiesgo, SolicitudCompras, TipoAprobacion, TipoCompras } from './comercial';
import type { Comunicacion } from './tiposV2';

export type TipoHallazgo = 'informacion' | 'hallazgo' | 'solicitud' | 'riesgo' | 'bloqueo' | 'recomendacion' | 'cambio_alcance' | 'seguimiento' | 'validacion';
export type Conocimiento = 'confirmado' | 'detectado' | 'inferido' | 'sugerido' | 'pendiente_validacion' | 'rechazado';
export type Severidad = 'baja' | 'media' | 'alta' | 'critica';
export type TipoRedaccion =
  | 'primer_contacto'
  | 'seguimiento'
  | 'seguimiento_oferta'
  | 'solicitud_reunion'
  | 'recapitulacion'
  | 'aclaracion'
  | 'confirmacion'
  | 'renovacion'
  | 'reactivacion'
  | 'agradecimiento'
  | 'respuesta';

export interface AgenteInfo {
  slug: string;
  nombre: string;
  rol: string;
  iniciales: string;
}
export interface AgenteComercial extends AgenteInfo {
  responsabilidad: string;
  eventos: string[];
  parametros: Record<string, unknown>;
  activo: boolean;
  tipo: string;
}
export interface AgenteEstado extends AgenteComercial {
  estado: string;
  trabajo: string;
  abiertos: number;
  altos: number;
  acciones_7d: number;
  ultima_ejecucion: string | null;
}
export interface Evidencia {
  texto: string;
  fuente: string;
  fecha?: string | null;
}
export interface AccionSugerida {
  tipo: string;
  texto: string;
  automatica?: boolean;
  fecha?: string | null;
  datos?: Record<string, unknown>;
  indice?: number;
  estado?: 'hecha' | 'descartada';
  objeto?: string | null;
}
export interface AccionGenerada {
  id: number;
  agente: string;
  agente_info: AgenteInfo;
  tipo: string;
  descripcion: string;
  objeto: string | null;
  responsable: string | null;
  fecha: string | null;
  automatica: boolean;
  confirmado_por: string | null;
  creado: string;
  estado_objeto: { tipo: string; estado: string; texto?: string | null; id?: number | string; fecha?: string | null } | null;
}
export interface Hallazgo {
  id: number;
  agente: string;
  agente_info: AgenteInfo;
  rol_agente: string;
  comercial: string | null;
  oportunidad_id: string | null;
  cuenta_id: string | null;
  contacto_id: string | null;
  tipo: TipoHallazgo;
  conocimiento: Conocimiento;
  severidad: Severidad;
  resumen: string;
  detalle: string | null;
  evidencias: Evidencia[];
  acciones_sugeridas: AccionSugerida[];
  evaluaciones: { agente: string; agente_info: AgenteInfo; rol: string; texto: string }[];
  decision: { agente: string; agente_info: AgenteInfo; rol: string; texto: string } | null;
  requiere_usuario: boolean;
  fuente: string | null;
  objeto: string | null;
  clave: string;
  estado: 'abierto' | 'atendido' | 'descartado' | 'resuelto';
  escalado: string | null;
  resuelto: string | null;
  creado: string;
  actualizado: string;
  oportunidad: { id: string; codigo: string; nombre: string; etapa: Etapa } | null;
  cuenta: { id: string; nombre: string } | null;
  acciones_generadas: AccionGenerada[];
}
export interface MiEquipo {
  comercial: string;
  comercial_nombre: string | null;
  agentes: AgenteEstado[];
  ultima_revision: string | null;
  hallazgos: Hallazgo[];
}
export interface ItemPreparado {
  tipo: 'borrador' | 'preparacion' | 'resumen_cuenta' | 'recomendacion_fecha';
  id: number;
  texto: string;
  oportunidad_id: string | null;
  codigo?: string;
  estado?: string | null;
  objeto?: string | null;
  agente: AgenteInfo;
}
export interface MiDia {
  saludo: string;
  fecha: string;
  coordinador: AgenteInfo;
  prioridad_alta: Hallazgo[];
  hoy: { reuniones: number; llamadas: number; seguimientos: number; tareas: number; actividades: Actividad[] };
  preparado: ItemPreparado[];
  resumen_preparado: { borradores: number; preparaciones: number; resumenes_cuenta: number; recomendaciones_fecha: number };
}
export interface MapaDecision {
  grupos: { rol: string; etiqueta: string; contactos: Pick<Contacto, 'id' | 'nombre' | 'apellido' | 'cargo' | 'correo' | 'rol_decision' | 'principal'>[] }[];
  sin_rol: Pick<Contacto, 'id' | 'nombre' | 'apellido' | 'cargo' | 'correo' | 'rol_decision' | 'principal'>[];
  faltan: string[];
  principal: Pick<Contacto, 'id' | 'nombre' | 'apellido'> | null;
}
export interface EquipoOportunidad {
  agentes: (AgenteInfo & { hechos: string[] })[];
  hallazgos: Hallazgo[];
  riesgo: { nivel: NivelRiesgo | null; puntaje: number | null; razones: { peso: number; texto: string; fuente: string }[]; tendencia: string | null; evaluado: string | null };
  mapa: MapaDecision;
}
export interface Ejecucion {
  id: number;
  agente: string;
  agente_info: AgenteInfo;
  version: string;
  disparador: string;
  comercial: string | null;
  comercial_nombre: string | null;
  oportunidad_id: string | null;
  codigo: string | null;
  contexto: Record<string, unknown>;
  salida: unknown;
  estado: 'ok' | 'sin_resultado' | 'error';
  error: string | null;
  duracion_ms: number | null;
  solicitado_por: string | null;
  creado: string;
  n_salidas: number;
}
export interface InteligenciaCuenta {
  cuenta: { id: string; nombre: string; sector: string | null; ciudad: string | null; tipo: string };
  resumen: string;
  vendido: number;
  lineas_adquiridas: { linea: string; valor: number }[];
  ganadas: OpCorta[];
  perdidas: OpCorta[];
  abiertas: OpCorta[];
  contactos: (Pick<Contacto, 'id' | 'nombre' | 'apellido' | 'cargo' | 'correo' | 'rol_decision' | 'principal'> & { ultimo_contacto: string | null })[];
  casos_preventa: { case_id: string; tema: string; estado: string; creado: string }[];
  renovaciones: { codigo: string; nombre: string; vigencia_fin: string; dias: number }[];
  actividad_reciente: { tipo: string; fecha: string; titulo: string; detalle?: string | null }[];
}
export interface OpCorta {
  id: string;
  codigo: string;
  nombre: string;
  etapa: Etapa;
  valor: number | null;
  valor_cop: number | null;
  moneda: Moneda;
  linea: string | null;
  cerrada: string | null;
  fecha_cierre: string | null;
  motivo_perdida: string | null;
  propietario_nombre?: string | null;
  case_id: string | null;
}
export interface Competidor {
  competidor: string;
  enfrentados: number;
  ganados_upk: number;
  perdidos_upk: number;
  abiertos: number;
  valor_perdido: number;
  lineas: string[];
  motivos: Record<string, number>;
  tasa_victoria_upk: number | null;
  fuentes: { codigo: string; oportunidad_id: string; etapa: Etapa; linea: string | null; fecha: string; motivo: string | null; detalle: string | null; cuenta: string }[];
}
export interface Competencia {
  competidores: Competidor[];
  motivos_perdida: { motivo: string; n: number; valor: number }[];
  menciones_en_correos: { correo_id: number; oportunidad_id: string; fecha: string; texto: string; evidencia: string; codigo: string }[];
}
export interface PreparacionReunion {
  actividad: Actividad;
  cuenta: { id: string; nombre: string; sector: string | null; ciudad: string | null; tipo: string } | null;
  oportunidad: { id: string; codigo: string; nombre: string; etapa: Etapa; valor: number | null; valor_cop: number | null; moneda: Moneda; fecha_cierre: string | null; proximo_paso: string | null; proximo_paso_fecha: string | null; riesgo_nivel: NivelRiesgo | null; competidores: string[] } | null;
  propuesta: { offer_id: string; total_sin_iva: number | null; rentabilidad: number | null } | null;
  ultima_comunicacion: { id: number; direccion: string; remitente: string | null; remitente_nombre: string | null; asunto: string; fecha: string; resumen: string | null } | null;
  pendientes: { tipo: string; texto: string; lado?: string; fecha?: string | null }[];
  riesgos: { peso: number; texto: string; fuente: string }[];
  objetivo_recomendado: string | null;
  contactos: Contacto[];
}
export interface Tableros {
  desde: string;
  hasta: string;
  perdidas_por_motivo: { motivo: string; n: number; valor: number; oportunidades: { id: string; codigo: string; nombre: string; competidores: string[] }[] }[];
  conversion: { etapa: Etapa; etiqueta: string; n: number; tasa_desde_anterior: number | null; tasa_desde_inicio: number | null }[];
  creadas: number;
  desempeno: {
    propietario: string;
    nombre: string;
    abiertas: number;
    pipeline: number;
    ponderado: number;
    ganado: number;
    n_ganadas: number;
    perdido: number;
    n_perdidas: number;
    tasa_cierre: number | null;
    ciclo_dias: number | null;
    actividades: number;
    sin_proximo_paso: number;
    riesgo_alto: number;
  }[];
  inspeccion: {
    id: string;
    codigo: string;
    nombre: string;
    cuenta_nombre: string;
    propietario_nombre: string | null;
    valor: number | null;
    moneda: Moneda;
    valor_cop: number;
    etapa: Etapa;
    forecast_categoria: string;
    salud: 'verde' | 'amarillo' | 'rojo' | null;
    riesgo_nivel: NivelRiesgo | null;
    tendencia: string | null;
    dias_en_etapa: number | null;
    ultima_actividad: string | null;
    proximo_paso: string | null;
    proximo_paso_fecha: string | null;
    caso_estado: string | null;
    case_id: string | null;
    fecha_cierre: string | null;
    cambios_cierre: number;
    cambios_semana: { campo: string; anterior: string | null; nuevo: string | null }[];
  }[];
  totales: { ganado: number; perdido: number; n_ganadas: number; n_perdidas: number };
}

const R = '/api/comercial';
const items = async <T,>(p: Promise<{ items: T[] }>) => (await p).items;

export const equipo = {
  agentes: () => solicitar<{ items: AgenteComercial[]; gobierno: Record<string, number> }>({ metodo: 'GET', ruta: `${R}/equipo/agentes` }),
  configurar: (slug: string, d: { activo: boolean; parametros: Record<string, unknown> }) => solicitar<AgenteComercial>({ metodo: 'PUT', ruta: `${R}/equipo/agentes/${slug}`, cuerpo: d }),
  miEquipo: (comercial?: string) => solicitar<MiEquipo>({ metodo: 'GET', ruta: `${R}/equipo`, query: comercial ? { comercial } : undefined }),
  miDia: (comercial?: string) => solicitar<MiDia>({ metodo: 'GET', ruta: `${R}/equipo/mi-dia`, query: comercial ? { comercial } : undefined }),
  revisar: (d: { comercial?: string; oportunidad_id?: string } = {}) => solicitar<{ hallazgos: number; nuevos: number }>({ metodo: 'POST', ruta: `${R}/equipo/revisar`, cuerpo: d }),
  hallazgos: (q: { comercial?: string; agente?: string; estado?: string; oportunidad_id?: string; cuenta_id?: string } = {}) =>
    items(solicitar<{ items: Hallazgo[] }>({ metodo: 'GET', ruta: `${R}/hallazgos`, query: q })),
  resolver: (id: number, estado: 'atendido' | 'descartado', motivo?: string) => solicitar<Hallazgo>({ metodo: 'POST', ruta: `${R}/hallazgos/${id}/resolver`, cuerpo: { estado, motivo } }),
  ejecutar: (id: number, indice: number, datos: Record<string, unknown> = {}) => solicitar<Hallazgo>({ metodo: 'POST', ruta: `${R}/hallazgos/${id}/acciones/${indice}`, cuerpo: datos }),
  ejecuciones: (q: { comercial?: string; agente?: string; oportunidad_id?: string } = {}) => items(solicitar<{ items: Ejecucion[] }>({ metodo: 'GET', ruta: `${R}/ejecuciones`, query: q })),
  deOportunidad: (id: string) => solicitar<EquipoOportunidad>({ metodo: 'GET', ruta: `${R}/oportunidades/${id}/equipo` }),
  redactar: (id: string, d: { tipo: TipoRedaccion; contacto_id?: string | null; correo_id?: number | null; indicaciones?: string | null }) =>
    solicitar<Comunicacion & { generado_por: string }>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/redactar`, cuerpo: d }),
  inteligencia: (cuentaId: string) => solicitar<InteligenciaCuenta>({ metodo: 'GET', ruta: `${R}/cuentas/${cuentaId}/inteligencia` }),
  competencia: (propietario?: string) => solicitar<Competencia>({ metodo: 'GET', ruta: `${R}/competencia`, query: propietario ? { propietario } : undefined }),
  preparacion: (actividadId: string) => solicitar<PreparacionReunion>({ metodo: 'GET', ruta: `${R}/actividades/${actividadId}/preparacion` }),
  tableros: (q: { desde?: string; hasta?: string; propietario?: string } = {}) => solicitar<Tableros>({ metodo: 'GET', ruta: `${R}/tableros`, query: q }),

  negociacion: (id: string) => solicitar<NegociacionVista>({ metodo: 'GET', ruta: `${R}/oportunidades/${id}/negociacion` }),
  guardarNegociacion: (id: string, d: Partial<Negociacion>) => solicitar<NegociacionVista>({ metodo: 'PUT', ruta: `${R}/oportunidades/${id}/negociacion`, cuerpo: d }),
  aprobaciones: (q: { estado?: string; oportunidad_id?: string } = {}) => items(solicitar<{ items: AprobacionComercial[] }>({ metodo: 'GET', ruta: `${R}/aprobaciones`, query: q })),
  solicitarAprobacion: (id: string, d: { tipo: TipoAprobacion; detalle: string; valor?: number | null }) =>
    solicitar<AprobacionComercial>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/aprobaciones`, cuerpo: d }),
  decidirAprobacion: (id: number, decision: 'aprobada' | 'rechazada', comentario?: string) =>
    solicitar<AprobacionComercial>({ metodo: 'POST', ruta: `${R}/aprobaciones/${id}/decidir`, cuerpo: { decision, comentario } }),
  cancelarAprobacion: (id: number) => solicitar<AprobacionComercial>({ metodo: 'POST', ruta: `${R}/aprobaciones/${id}/cancelar`, cuerpo: {} }),
  compras: (q: { estado?: string; oportunidad_id?: string } = {}) => items(solicitar<{ items: SolicitudCompras[] }>({ metodo: 'GET', ruta: `${R}/compras`, query: q })),
  solicitarCompras: (id: string, d: { tipo: TipoCompras; detalle: string; fecha_requerida?: string | null }) =>
    solicitar<SolicitudCompras>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/compras`, cuerpo: d }),
  responderCompras: (id: number, d: { estado: 'en_gestion' | 'respondida' | 'cancelada'; respuesta?: string }) =>
    solicitar<SolicitudCompras>({ metodo: 'POST', ruta: `${R}/compras/${id}/responder`, cuerpo: d }),
};

export const ETIQUETA_TIPO_HALLAZGO: Record<TipoHallazgo, string> = {
  informacion: 'Información',
  hallazgo: 'Hallazgo',
  solicitud: 'Solicitud',
  riesgo: 'Riesgo',
  bloqueo: 'Bloqueo',
  recomendacion: 'Recomendación',
  cambio_alcance: 'Cambio de alcance',
  seguimiento: 'Seguimiento',
  validacion: 'Validación',
};
export const ETIQUETA_CONOCIMIENTO: Record<Conocimiento, string> = {
  confirmado: 'Confirmado',
  detectado: 'Detectado',
  inferido: 'Inferido',
  sugerido: 'Sugerido',
  pendiente_validacion: 'Pendiente de validación',
  rechazado: 'Rechazado',
};
export const ETIQUETA_SEVERIDAD: Record<Severidad, string> = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
export const ETIQUETA_REDACCION: Record<TipoRedaccion, string> = {
  primer_contacto: 'Primer contacto',
  seguimiento: 'Seguimiento',
  seguimiento_oferta: 'Seguimiento de oferta',
  solicitud_reunion: 'Solicitud de reunión',
  recapitulacion: 'Recapitulación',
  aclaracion: 'Aclaración',
  confirmacion: 'Confirmación',
  renovacion: 'Renovación',
  reactivacion: 'Reactivación',
  agradecimiento: 'Agradecimiento',
  respuesta: 'Respuesta',
};
